import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
  FormControlLabel,
  Switch,
  Snackbar,
  Tooltip as MuiTooltip,
  IconButton,
  Menu,
  ListItemIcon,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Email as EmailIcon,
  Business as OrgIcon,
  CalendarToday as DateIcon,
  CheckCircle as EnabledIcon,
  Block as DisabledIcon,
  FileDownload as ExportIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreIcon,
  Password as PasswordIcon,
  Send as SendIcon,
  LockReset as LockResetIcon,
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { adminApi, UserDetails, Job, Organization } from '../services/api';
import JobRunsTable from '../components/JobRunsTable';
import {
  AdminAssignableTier,
  TIER_CATALOGUE,
  TIER_CODES_ORDERED,
  TierSelectValue,
} from '../constants/subscription-tiers';
import { IS_BROKER_ADMIN } from '../config/admin-build';

const UserDetailPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  
  const [user, setUser] = useState<UserDetails | null>(null);
  const [usageHistory, setUsageHistory] = useState<any[]>([]);
  const [usageByService, setUsageByService] = useState<any[]>([]);
  const [recentJobs, setRecentJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Export dialog state
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportPeriod, setExportPeriod] = useState<string>('3');
  const [exporting, setExporting] = useState(false);
  const [resending, setResending] = useState(false);
  const [resettingMfa, setResettingMfa] = useState(false);

  // Out-of-band onboarding (Trello #239)
  const [resettingAccess, setResettingAccess] = useState(false);
  const [resetAccessDialogOpen, setResetAccessDialogOpen] = useState(false);
  const [resetAccessResult, setResetAccessResult] = useState<{ email: string; temporaryPassword: string } | null>(null);

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    organizationId: '',
    isPaid: false,
    maxRunsPerMonth: 50,
    maxDocChatLimit: 20,
    maxResearchLimit: 20,
    firstName: '',
    lastName: '',
    embeddings: false,
    // Per-user tier seat. '' = no tier; admin picks Bronze/Silver/Gold to
    // create the seat (backend snaps priceZAR + run quota from PRICING_CENTS).
    tier: '' as TierSelectValue,
  });
  const [saving, setSaving] = useState(false);
  const [organizations, setOrganizations] = useState<{ id: string; name: string }[]>([]);
  // User's org — fetched on mount so the tier dropdown can be gated by
  // stack === 'broker-ai' (per-user tier seats are broker-AI-only).
  const [userOrg, setUserOrg] = useState<Organization | null>(null);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
  const [deleting, setDeleting] = useState(false);
  // Dialog-scoped delete error. Kept SEPARATE from the page-level `error` on
  // purpose: `error` triggers a full-page early-return (`if (error)` below), so
  // routing a delete failure through it would unmount the dialog. This stays in
  // the dialog so the admin reads the rejection reason in context.
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Snackbar state for copy feedback
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Overflow ("⋯") menu for secondary / destructive actions, so the header
  // isn't a wall of equal-weight buttons (Disable / Delete / Reset MFA hide here).
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState<null | HTMLElement>(null);
  const closeActionsMenu = () => setActionsMenuAnchor(null);

  const ADMIN_ELIGIBLE_DOMAINS = [
    'solicitors.co.za',
    'iamanagement.co.za',
  ];

  const isAdminEligible = (email: string) => {
    const domain = email.split('@')[1]?.toLowerCase() || '';
    return ADMIN_ELIGIBLE_DOMAINS.includes(domain);
  };

  const isAdmin = user?.groups?.includes('Admins') ?? false;

  const copyToClipboard = (value: string, feedback: string) => {
    navigator.clipboard.writeText(value).then(() => {
      setSnackbarMessage(feedback);
      setSnackbarOpen(true);
    }).catch(() => {
      setSnackbarMessage('Copy failed — select and copy the value manually');
      setSnackbarOpen(true);
    });
  };

  // Clear the temporary password from memory when the dialog closes (matches
  // the "shown once" promise; keeps the credential's in-memory lifetime tight).
  const closeResetAccessDialog = () => {
    setResetAccessDialogOpen(false);
    setResetAccessResult(null);
  };

  const S3_REGION = 'af-south-1';

  const getS3UploadsUrl = (uid: string) => {
    const bucket = import.meta.env.VITE_UPLOADS_BUCKET || '';
    return `https://${S3_REGION}.console.aws.amazon.com/s3/buckets/${bucket}?prefix=uploads/${uid}/&region=${S3_REGION}`;
  };

  const getS3OutputUrl = (outputLocation: string) => {
    const bucket = import.meta.env.VITE_OUTPUTS_BUCKET || '';
    return `https://${S3_REGION}.console.aws.amazon.com/s3/object/${bucket}?prefix=${outputLocation}&region=${S3_REGION}`;
  };

  // Status helper functions
  const getStatusChipProps = (status: string) => {
    switch (status) {
      case 'CONFIRMED':
        return { label: 'Confirmed', color: 'success' as const };
      case 'FORCE_CHANGE_PASSWORD':
        return { label: 'Invited - Pending Login', color: 'warning' as const };
      case 'UNCONFIRMED':
        return { label: 'Pending Verification', color: 'warning' as const };
      case 'ARCHIVED':
        return { label: 'Archived', color: 'default' as const };
      case 'COMPROMISED':
        return { label: 'Compromised', color: 'error' as const };
      case 'UNKNOWN':
        return { label: 'Unknown', color: 'default' as const };
      case 'RESET_REQUIRED':
        return { label: 'Reset Required', color: 'warning' as const };
      // Not a Cognito UserStatus — the backend sends it when there is no account
      // at all (Trello #420). Without a case here the default arm below prints
      // the raw enum, `NO_LOGIN_ACCOUNT`, into the Status field.
      case 'NO_LOGIN_ACCOUNT':
        return { label: 'No login account', color: 'default' as const };
      default:
        return { label: status, color: 'default' as const };
    }
  };

  const handleResendInvitation = async () => {
    if (!userId) return;
    try {
      setResending(true);
      setError(null);
      setSuccessMessage(null);
      await adminApi.resendUserInvitation(userId);
      setSuccessMessage('Invitation email resent successfully');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to resend invitation');
    } finally {
      setResending(false);
    }
  };

  const handleResendVerification = async () => {
    if (!userId) return;
    try {
      setResending(true);
      setError(null);
      setSuccessMessage(null);
      await adminApi.resendUserVerification(userId);
      setSuccessMessage('Verification code resent successfully');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to resend verification code');
    } finally {
      setResending(false);
    }
  };

  // Out-of-band onboarding (Trello #239): generate a temporary password the
  // admin conveys directly to a user whose invite email is blocked.
  const handleResetAccess = async () => {
    if (!userId) return;
    try {
      setResettingAccess(true);
      setError(null);
      setSuccessMessage(null);
      const data = await adminApi.resetUserAccess(userId);
      setResetAccessResult({ email: data.email, temporaryPassword: data.temporaryPassword });
      setResetAccessDialogOpen(true);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to generate login password');
    } finally {
      setResettingAccess(false);
    }
  };

  const handleResetMfa = async () => {
    if (!userId || !window.confirm('Are you sure you want to reset MFA for this user? They will need to set up MFA again on their next login if their organization requires it.')) return;
    try {
      setResettingMfa(true);
      setError(null);
      setSuccessMessage(null);
      await adminApi.resetUserMfa(userId);
      setSuccessMessage('MFA has been reset successfully. The user will need to set up MFA again.');
      const response = await adminApi.getUser(userId);
      if (response.success) {
        setUser(response.data.user);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to reset MFA');
    } finally {
      setResettingMfa(false);
    }
  };

  useEffect(() => {
    const fetchUserDetails = async () => {
      if (!userId) return;

      try {
        setLoading(true);
        const response = await adminApi.getUser(userId);

        if (response.success) {
          setUser(response.data.user);
          setUsageHistory(response.data.usage?.history || []);
          setUsageByService(response.data.usage?.byService || []);
          setRecentJobs(response.data.recentJobs || []);

          // Fetch the user's org so the tier dropdown can be gated by stack.
          // Non-blocking — failure leaves the dropdown hidden (safer default;
          // backend rejects any cross-stack write anyway).
          const orgId = response.data.user?.organizationId;
          if (orgId) {
            try {
              const orgResp = await adminApi.getOrganization(orgId);
              if (orgResp.success) {
                setUserOrg(orgResp.data.organization as Organization);
              }
            } catch (orgErr) {
              console.warn('Failed to fetch user org for tier dropdown:', orgErr);
            }
          }
        }
      } catch (err: any) {
        setError(err.response?.data?.message || err.message || 'Failed to load user details');
      } finally {
        setLoading(false);
      }
    };

    const fetchOrganizations = async () => {
      try {
        const response = await adminApi.listOrganizations();
        if (response.success) {
          setOrganizations(response.data.organizations.map((org: any) => ({
            // CRITICAL FIX: API returns 'id', not 'organizationId'
            // Using org.name as fallback was storing NAME instead of ID!
            id: org.id || org.organizationId || org.name,
            name: org.name || org.displayName || org.id
          })));
        }
      } catch (err) {
        console.warn('Failed to fetch organizations:', err);
      }
    };

    fetchUserDetails();
    fetchOrganizations();
  }, [userId]);

  // Per-user tier seats apply to broker-AI org users only. The dropdown is
  // hidden for IA users (backend rejects 400 for non-broker-AI target orgs).
  // The seat IS visible regardless of `sharedRunPool` mode — billing is now
  // always per-org but the tier is always per-user.
  const tierDropdownEligible = userOrg?.stack === 'broker-ai';

  // Searchable Organization picker options: the implicit "default-org"
  // sentinel pinned first (matching the prior native <Select> ordering), then
  // every fetched org sorted alphabetically so the typeahead list reads
  // predictably (the org list can be very long).
  const orgOptions = React.useMemo(
    () => [
      { id: 'default-org', name: 'default-org' },
      ...[...organizations].sort((a, b) => a.name.localeCompare(b.name)),
    ],
    [organizations],
  );

  const handleOpenEditDialog = () => {
    if (user) {
      setEditForm({
        organizationId: user.organizationId || 'default-org',
        isPaid: user.isPaid,
        maxRunsPerMonth: user.maxRunsPerMonth || 50,
        maxDocChatLimit: user.maxDocChatLimit || 20,
        maxResearchLimit: user.maxResearchLimit || 20,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        embeddings: user.embeddingsEnabled,
        tier: (user.tier as TierSelectValue) || '',
      });
      setEditDialogOpen(true);
    }
  };

  const handleSaveUser = async () => {
    if (!user) return;

    // Tier seat: send the chosen code, or `null` to clear the seat. Only
    // include the field for broker-AI orgs — backend would 400 otherwise.
    const tierUpdate: AdminAssignableTier | null | undefined = tierDropdownEligible
      ? editForm.tier === ''
        ? null
        : (editForm.tier as AdminAssignableTier)
      : undefined;

    try {
      setSaving(true);
      setError(null);

      await adminApi.updateUser(user.userId, {
        organizationId: editForm.organizationId,
        isPaid: editForm.isPaid,
        maxRunsPerMonth: editForm.maxRunsPerMonth,
        // Omit-on-broker: the broker build no longer renders these inputs, so it
        // must not write them — leaving the stored values untouched rather than
        // persisting state nobody can see or change (Trello #380 follow-up).
        maxDocChatLimit: IS_BROKER_ADMIN ? undefined : editForm.maxDocChatLimit,
        maxResearchLimit: IS_BROKER_ADMIN ? undefined : editForm.maxResearchLimit,
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        embeddings: editForm.embeddings,
        tier: tierUpdate,
      });

      // Refresh user data
      const response = await adminApi.getUser(user.userId);
      if (response.success) {
        setUser(response.data.user);
      }

      setEditDialogOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!user) return;

    try {
      if (user.isEnabled) {
        await adminApi.disableUser(user.userId);
      } else {
        await adminApi.enableUser(user.userId);
      }
      // Refresh user data
      const response = await adminApi.getUser(user.userId);
      if (response.success) {
        setUser(response.data.user);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to update user status');
    }
  };

  const handleExportUser = async (format: 'excel' | 'pdf') => {
    if (!user) return;
    
    try {
      setExporting(true);
      const months = parseInt(exportPeriod);
      
      // Use the per-user job history export endpoint
      const response = await adminApi.exportUserJobs(user.userId, {
        months,
        format
      });
      
      if (response.success && response.data.downloadUrl) {
        window.open(response.data.downloadUrl, '_blank');
        
        // Show warning if results were truncated
        if (response.data.truncated && response.data.warning) {
          setError(`⚠️ ${response.data.warning}. Try using a shorter time period for complete results.`);
        }
      }
      
      setExportDialogOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to export user job history');
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!user) return;

    try {
      setDeleting(true);
      setDeleteError(null);

      const result = await adminApi.deleteUser(user.userId);

      // A 2xx does NOT mean both stores were updated. The backend deletes the
      // Cognito account first and cannot roll it back, so a failed user-profiles
      // write still returns success — with `profileSyncFailed` set. That exact
      // combination is what creates an orphaned profile row (backend Trello
      // #420), so it must not be reported as a clean delete and must not
      // navigate away: the admin needs to retry the delete, which will now find
      // no Cognito account and remove the row alone.
      if (result?.data?.profileSyncFailed) {
        setDeleteError(
          'The login account was deleted, but the profile record could NOT be removed — ' +
          'it will still show on the organisation\'s member list. Press Delete again to remove it.',
        );
        return;
      }

      // Navigate back to users list on success
      navigate('/users');
    } catch (err: any) {
      // Keep the delete dialog OPEN on failure and show the reason IN the dialog
      // (via dialog-scoped `deleteError`, not the page-level `error` which would
      // unmount the page) — e.g. the backend's 409 "this user is the org owner,
      // transfer ownership first" guard. Closing the dialog or routing through
      // the full-page error would leave the admin without in-context feedback.
      setDeleteError(err.response?.data?.message || err.message || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/users')} sx={{ mb: 2 }}>
          Back to Users
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!user) {
    return (
      <Box>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/users')} sx={{ mb: 2 }}>
          Back to Users
        </Button>
        <Alert severity="warning">User not found</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Button startIcon={<BackIcon />} onClick={() => navigate('/users')} sx={{ mb: 3 }}>
        Back to Users
      </Button>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Orphaned profile row (backend Trello #420). This page used to 404 for
          these, which is how they became un-removable: they show on the
          organisation's Members table, that table's only action is View, and
          View landed on "User not found" — a screen with no actions on it.
          Say plainly what the row is, and leave Delete as the one live action. */}
      {user.cognitoAccountMissing && (
        <Alert severity="warning" sx={{ mb: 3 }} data-testid="orphan-profile-banner">
          <strong>No login account.</strong> This is a leftover profile record — it appears on
          the organisation&apos;s member list, but there is no account behind it, so this person
          cannot sign in and nothing here can be disabled, enabled or re-invited.
          Use <strong>Delete user</strong> to remove the record from the member list.
        </Alert>
      )}

      {/* User Info Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box>
              <Typography variant="h5" fontWeight={700}>
                {user.firstName || user.lastName 
                  ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                  : user.email
                }
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <EmailIcon fontSize="small" color="action" />
                <Typography color="text.secondary">{user.email}</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* An orphan is neither Active nor Disabled — both of those describe
                  a login account, and there isn't one. Showing "Active" here was
                  the most misleading part of the old page. */}
              <Chip
                icon={user.cognitoAccountMissing
                  ? <DisabledIcon />
                  : (user.isEnabled ? <EnabledIcon /> : <DisabledIcon />)}
                label={user.cognitoAccountMissing
                  ? 'No login account'
                  : (user.isEnabled ? 'Active' : 'Disabled')}
                color={user.cognitoAccountMissing
                  ? 'default'
                  : (user.isEnabled ? 'success' : 'error')}
              />
              {/* Edit writes Cognito attributes (paid flag, per-user limits), so
                  it 404s on a row with no account. Greyed with the reason rather
                  than letting an admin fill the dialog in and hit a raw error. */}
              <MuiTooltip
                title={user.cognitoAccountMissing
                  ? 'There is no login account to edit — only Delete applies here'
                  : ''}
              >
                <span>
                  <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={handleOpenEditDialog}
                    disabled={Boolean(user.cognitoAccountMissing)}
                  >
                    Edit
                  </Button>
                </span>
              </MuiTooltip>
              <Button
                variant="outlined"
                startIcon={<ExportIcon />}
                onClick={() => setExportDialogOpen(true)}
              >
                Export
              </Button>
              <MuiTooltip title="More actions">
                <IconButton
                  aria-label="More actions"
                  onClick={(e) => setActionsMenuAnchor(e.currentTarget)}
                  sx={{ border: '1px solid', borderColor: 'divider' }}
                >
                  <MoreIcon />
                </IconButton>
              </MuiTooltip>
              <Menu
                anchorEl={actionsMenuAnchor}
                open={Boolean(actionsMenuAnchor)}
                onClose={closeActionsMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                {user.mfaEnabled && (
                  <MenuItem
                    onClick={() => { closeActionsMenu(); handleResetMfa(); }}
                    disabled={resettingMfa}
                  >
                    <ListItemIcon><LockResetIcon fontSize="small" /></ListItemIcon>
                    {resettingMfa ? 'Resetting MFA…' : 'Reset MFA'}
                  </MenuItem>
                )}
                {user.mfaEnabled && <Divider />}
                {/* Disable/Enable act on the Cognito account. With no account
                    there is nothing to act on — the backend refuses the enable
                    outright — so the item is not offered at all.
                    HIDDEN rather than greyed-with-a-tooltip on purpose: a
                    Tooltip needs a wrapper element to catch hover on a disabled
                    child, and a <span> inside a MenuList renders as
                    `ul > span > li`, which is invalid HTML (ai_review !229).
                    Reset MFA above is already conditional on `user.mfaEnabled`,
                    so hiding is this menu's own idiom — and the orphan banner
                    already states that these actions do not apply. */}
                {!user.cognitoAccountMissing && (
                  <MenuItem onClick={() => { closeActionsMenu(); handleToggleStatus(); }}>
                    <ListItemIcon>
                      {user.isEnabled
                        ? <DisabledIcon fontSize="small" />
                        : <EnabledIcon fontSize="small" color="success" />}
                    </ListItemIcon>
                    {user.isEnabled ? 'Disable user' : 'Enable user'}
                  </MenuItem>
                )}
                <MenuItem
                  onClick={() => { closeActionsMenu(); setDeleteError(null); setDeleteConfirmEmail(''); setDeleteDialogOpen(true); }}
                  sx={{ color: 'error.main' }}
                >
                  <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                  Delete user…
                </MenuItem>
              </Menu>
            </Box>
          </Box>

          {/* Onboarding callout banner — surfaces the first-login / verification
              actions that were previously buried as tiny text links next to the
              status chip (impossible for admins to discover). Only rendered while
              the user hasn't completed onboarding. */}
          {(user.status === 'FORCE_CHANGE_PASSWORD' || user.status === 'UNCONFIRMED') && (
            <Box
              sx={{
                mt: 3,
                p: 2,
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'stretch', md: 'center' },
                justifyContent: 'space-between',
                gap: 2,
                borderRadius: 2,
                bgcolor: '#fffbeb',
                border: '1px solid #fde68a',
                borderLeft: '4px solid #f59e0b',
              }}
            >
              <Box>
                <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#92400e' }}>
                  {user.status === 'FORCE_CHANGE_PASSWORD'
                    ? "This user hasn't completed their first login"
                    : "This user hasn't verified their email yet"}
                </Typography>
                <Typography variant="body2" sx={{ color: '#a16207', mt: 0.25 }}>
                  {user.status === 'FORCE_CHANGE_PASSWORD'
                    ? `Invited ${new Date(user.createdAt).toLocaleDateString()}. If the invite email isn't arriving, hand them a temporary password directly (e.g. via WhatsApp).`
                    : 'They need to enter the verification code sent to their email before they can sign in.'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, flexWrap: 'wrap' }}>
                {user.status === 'FORCE_CHANGE_PASSWORD' ? (
                  <>
                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={<PasswordIcon />}
                      onClick={handleResetAccess}
                      disabled={resettingAccess}
                    >
                      {resettingAccess ? 'Generating…' : 'Generate temporary password'}
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<SendIcon />}
                      onClick={handleResendInvitation}
                      disabled={resending}
                    >
                      {resending ? 'Resending…' : 'Resend invitation email'}
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="contained"
                    startIcon={<SendIcon />}
                    onClick={handleResendVerification}
                    disabled={resending}
                  >
                    {resending ? 'Resending…' : 'Resend verification code'}
                  </Button>
                )}
              </Box>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">Organization</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <OrgIcon fontSize="small" color="action" />
                <Typography>{user.organizationId || 'default-org'}</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">Status</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <Chip
                  size="small"
                  {...getStatusChipProps(user.status)}
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">Created</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DateIcon fontSize="small" color="action" />
                <Typography>{new Date(user.createdAt).toLocaleDateString()}</Typography>
              </Box>
            </Grid>
            {/* The per-user limits are Cognito attributes. A row with no account
                has none, and the `|| '50'` fallback would state an allowance
                that nothing is enforcing — the same class of lie as labelling
                the row "Active" (Trello #420). Show a dash instead. */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">Monthly Limit</Typography>
              <Typography>
                {user.cognitoAccountMissing ? '—' : `${user.maxRunsPerMonth || '50'} runs`}
              </Typography>
            </Grid>
            {/* Per-user Doc Chat / Legal Research allowances — IA build only
                (Trello #380 follow-up). Broker-AI runs on credit billing, so both
                draw down the org's main credit balance and these per-user caps
                gate nothing; the org-level pair was removed for the same reason.
                The IA stack still meters them separately, so they stay there. */}
            {!IS_BROKER_ADMIN && (
              <>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">Doc Chat Limit</Typography>
                  <Typography>
                    {user.cognitoAccountMissing ? '—' : `${user.maxDocChatLimit || '20'} runs`}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">Research Limit</Typography>
                  <Typography>
                    {user.cognitoAccountMissing ? '—' : `${user.maxResearchLimit || '20'} runs`}
                  </Typography>
                </Grid>
              </>
            )}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>Paid User</Typography>
              <Chip label={user.isPaid ? 'Yes' : 'No'} size="small" color={user.isPaid ? 'success' : 'default'} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>MFA Enabled</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip label={user.mfaEnabled ? 'Yes' : 'No'} size="small" color={user.mfaEnabled ? 'primary' : 'default'} />
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>Embeddings</Typography>
              <Chip label={user.embeddingsEnabled ? 'Enabled' : 'Disabled'} size="small" color={user.embeddingsEnabled ? 'primary' : 'default'} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>Groups</Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {user.groups.length > 0 
                  ? user.groups.map(g => <Chip key={g} label={g} size="small" />)
                  : <Typography variant="body2" color="text.secondary">None</Typography>
                }
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Usage Charts */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Usage History (Last 3 Months)
              </Typography>
              <Box sx={{ height: 250 }}>
                {usageHistory.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={[...usageHistory].reverse()}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="completedCount" name="Completed" fill="#38a169" />
                      <Bar dataKey="failedCount" name="Failed" fill="#e53e3e" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Typography color="text.secondary">No usage data</Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Usage by Service
              </Typography>
              {usageByService.length > 0 ? (
                <Box>
                  {usageByService.slice(0, 8).map((item, index) => (
                    <Box 
                      key={index} 
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        py: 1,
                        borderBottom: index < usageByService.length - 1 ? 1 : 0,
                        borderColor: 'divider'
                      }}
                    >
                      <Typography variant="body2">{item.service}</Typography>
                      <Chip label={item.count} size="small" />
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography color="text.secondary">No service data</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Jobs */}
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Recent Jobs
          </Typography>
          <JobRunsTable jobs={recentJobs} emptyMessage="No recent jobs" />
        </CardContent>
      </Card>

      {/* Reset-access (out-of-band onboarding) Dialog — Trello #239 */}
      <Dialog open={resetAccessDialogOpen} onClose={closeResetAccessDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Temporary login password generated</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Send these credentials to the user <strong>directly</strong> (e.g. WhatsApp or phone) — do not rely on email.
            They log in with them and will be prompted to set their own password. The temporary password is valid for 24 hours.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Email</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontFamily: 'monospace' }}>{resetAccessResult?.email}</Typography>
                <Button size="small" onClick={() => copyToClipboard(resetAccessResult?.email || '', 'Email copied')}>
                  Copy
                </Button>
              </Box>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Temporary password</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontFamily: 'monospace' }}>{resetAccessResult?.temporaryPassword}</Typography>
                <Button size="small" onClick={() => copyToClipboard(resetAccessResult?.temporaryPassword || '', 'Password copied')}>
                  Copy
                </Button>
              </Box>
            </Box>
          </Box>
          <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 2 }}>
            Shown once. Generating again produces a new password and invalidates this one.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeResetAccessDialog} variant="contained">Done</Button>
        </DialogActions>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Export User Data</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Export usage history for {user.firstName || user.email}
          </Typography>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Time Period</InputLabel>
            <Select
              value={exportPeriod}
              label="Time Period"
              onChange={(e) => setExportPeriod(e.target.value)}
            >
              <MenuItem value="1">Last 1 Month</MenuItem>
              <MenuItem value="2">Last 2 Months</MenuItem>
              <MenuItem value="3">Last 3 Months</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="outlined" 
            onClick={() => handleExportUser('pdf')}
            disabled={exporting}
          >
            {exporting ? <CircularProgress size={20} /> : 'PDF'}
          </Button>
          <Button 
            variant="contained" 
            onClick={() => handleExportUser('excel')}
            disabled={exporting}
          >
            {exporting ? <CircularProgress size={20} /> : 'Excel'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="First Name"
                value={editForm.firstName}
                onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Last Name"
                value={editForm.lastName}
                onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Autocomplete
                fullWidth
                disableClearable
                options={orgOptions}
                value={
                  orgOptions.find((o) => o.id === editForm.organizationId) ?? orgOptions[0]
                }
                getOptionLabel={(o) => o.name}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                onChange={(_e, selected) =>
                  setEditForm({ ...editForm, organizationId: selected.id })
                }
                renderInput={(params) => (
                  <TextField {...params} label="Organization" placeholder="Search organizations…" />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {option.name}
                  </li>
                )}
              />
            </Grid>
            {tierDropdownEligible && (
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth>
                  <InputLabel id="user-tier-label">Subscription Tier</InputLabel>
                  <Select
                    labelId="user-tier-label"
                    label="Subscription Tier"
                    value={editForm.tier}
                    onChange={(e) => setEditForm({ ...editForm, tier: e.target.value as TierSelectValue })}
                  >
                    <MenuItem value="">
                      <em>None — no seat (org admin / hyper-user override applies)</em>
                    </MenuItem>
                    {TIER_CODES_ORDERED.map((code) => {
                      const meta = TIER_CATALOGUE[code];
                      return (
                        <MenuItem key={code} value={code}>
                          {meta.displayName} — R {meta.monthlyAmountZAR.toLocaleString('en-ZA')} / mo, {meta.monthlyRunsQuota} credits
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                type="number"
                label="Monthly Run Limit"
                value={editForm.maxRunsPerMonth}
                onChange={(e) => setEditForm({ ...editForm, maxRunsPerMonth: parseInt(e.target.value) || 50 })}
                inputProps={{ min: 1 }}
              />
            </Grid>
            {/* Per-user Doc Chat / Legal Research limits — IA build only
                (Trello #380 follow-up). See the display tiles above. */}
            {!IS_BROKER_ADMIN && (
              <>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Doc Chat Limit"
                    value={editForm.maxDocChatLimit}
                    onChange={(e) => setEditForm({ ...editForm, maxDocChatLimit: parseInt(e.target.value) || 20 })}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Research Limit"
                    value={editForm.maxResearchLimit}
                    onChange={(e) => setEditForm({ ...editForm, maxResearchLimit: parseInt(e.target.value) || 20 })}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
              </>
            )}
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editForm.isPaid}
                    onChange={(e) => setEditForm({ ...editForm, isPaid: e.target.checked })}
                    color="success"
                  />
                }
                label="Paid User"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editForm.embeddings}
                    onChange={(e) => setEditForm({ ...editForm, embeddings: e.target.checked })}
                    color="primary"
                  />
                }
                label="Embeddings Enabled"
              />
            </Grid>
          </Grid>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSaveUser}
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>Delete User Account</DialogTitle>
        <DialogContent>
          {/* Surfaces a failed-delete reason (e.g. the backend 409 "this user is
              the org owner — transfer ownership first" guard) so the admin sees
              WHY the delete was rejected instead of the dialog closing silently. */}
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>Warning: This action cannot be undone!</strong>
            <br />
            This will permanently delete the user account from both Cognito and the database.
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            To confirm deletion, please type the user's email address below:
          </Typography>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 2 }}>
            {user?.email}
          </Typography>
          <TextField
            fullWidth
            label="Confirm Email Address"
            value={deleteConfirmEmail}
            onChange={(e) => setDeleteConfirmEmail(e.target.value)}
            placeholder={user?.email}
            disabled={deleting}
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={handleDeleteUser}
            disabled={deleting || deleteConfirmEmail.toLowerCase() !== user?.email.toLowerCase()}
            startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete User'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default UserDetailPage;

